//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Icon, Select, SelectItem } from 'unison';

import { instagramAccountActions } from '@instagram-account/redux/account/instagramAccountActions';
import { requestRightsActions } from '@legacy-components/rights-management/request-rights-actions';
import { ICuralateStore } from '@models/AppState';
import styled from 'styled-components';
import { ReduxDispatch } from 'app/curalate.store';
import Avatar from '@components/atoms/avatar/Avatar';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { personalizeRightsRequestMessage } from '@legacy-components/rights-management/request-rights-util';
import { getIsIncludeUsernameEnabled } from '@galleries/selectors/bulk-rights-selectors/getRightsPersonalizationFeatures';

const mapStateToProps = (state: ICuralateStore) => {
  return {
    requestRights: state.requestRights,
    session: state.session,
    instagram: state.instagram.account,

    // TODO: PD-249308 - remove rights_requests_include_username flag
    isIncludeUsernameEnabled: getIsIncludeUsernameEnabled(state),
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    actions: {
      requestRights: bindActionCreators(requestRightsActions, dispatch),
      instagram: bindActionCreators(instagramAccountActions, dispatch),
    },
  };
};

interface IOwnProps {
  className?: string;
  showLoadingFiller?: boolean;
  selectedItem?: IContentLibraryItem;
}

interface IOwnState {
  isLoaded: boolean;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  IOwnProps;

export class RotatingRightsMessages extends React.Component<Props, IOwnState> {
  public static defaultProps = {
    showLoadingFiller: true,
  };

  public state = {
    isLoaded: false,
  };

  public async componentDidMount() {
    const {
      actions: {
        instagram: { fetchDefaultInstagramAccount },
        requestRights: { requestRightsConfigurations },
      },
      instagram: { defaultInstagramAccount },
      requestRights: { areConfigurationsFetched },
    } = this.props;

    const loadingPromises = [];

    if (!defaultInstagramAccount) {
      loadingPromises.push(fetchDefaultInstagramAccount());
    }

    if (!areConfigurationsFetched) {
      loadingPromises.push(requestRightsConfigurations());
    }

    await Promise.all(loadingPromises);

    this.setState({ isLoaded: true });
  }

  public render() {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
      },
      instagram: { defaultInstagramAccount },
      session: { client },
      className,
      showLoadingFiller,
    } = this.props;

    if (!this.state.isLoaded) {
      return showLoadingFiller ? this.getLoadingFiller() : null;
    }

    const rightsConfigurations = this.getRightsConfigurations();

    return (
      <div className={className}>
        <Select
          handleChange={this.handleSelectChange}
          items={rightsConfigurations}
          value={byId[selectedConfigurationId].id.toString(10)}
          className='cu-w-100 select-popover-scroll'
        >
          {rightsConfigurations.map(({ label, value }) => (
            <SelectItem key={label} label={label} value={value} />
          ))}
        </Select>
        <RotatingRightsRequests>
          <RefreshIcon name='sync-alt' onClick={this.handleMessageChange} />
          <RotatingRightsRequestsContent>
            {this.getBrandIcon()}
            <div className='cu-flex-1 cu-text-body'>
              <span className='cu-text-body-2'>
                {defaultInstagramAccount
                  ? defaultInstagramAccount.username
                  : client.name}
                &nbsp;
              </span>
              {this.getCurrentRightsMessage()}
            </div>
          </RotatingRightsRequestsContent>
        </RotatingRightsRequests>
      </div>
    );
  }

  private getLoadingFiller = () => {
    const { className } = this.props;

    return (
      <div className={className}>
        <SelectLoadingFiller className='loading-element gradient' />
        <MessageLoadingFiller className='loading-element gradient' />
      </div>
    );
  };

  private handleSelectChange = (configurationValue: string) => {
    const {
      actions: {
        requestRights: { selectConfigurationId, selectMessageIndex },
      },
    } = this.props;

    const configurationId = parseInt(configurationValue, 10);
    selectConfigurationId(configurationId);
    selectMessageIndex(0);
  };

  private handleMessageChange = () => {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
        selectedMessageIndex,
      },
      actions: {
        requestRights: { selectMessageIndex },
      },
    } = this.props;

    const numMessages =
      byId[selectedConfigurationId].rightsRequestData.data.length;
    selectMessageIndex((selectedMessageIndex + 1) % numMessages);
  };

  private getCurrentRightsMessage = () => {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
        selectedMessageIndex,
      },
      selectedItem,
      isIncludeUsernameEnabled,
    } = this.props;

    const message =
      byId[selectedConfigurationId].rightsRequestData.data[selectedMessageIndex]
        .message;

    return personalizeRightsRequestMessage(
      isIncludeUsernameEnabled,
      message,
      selectedItem?.socialDetails?.user?.username ?? '',
    );
  };

  private getBrandIcon = () => {
    const {
      session: { client },
      instagram: { defaultInstagramAccount },
    } = this.props;

    const props = defaultInstagramAccount
      ? { imageUrl: defaultInstagramAccount.profileImageUrl }
      : { username: client.name };

    return <Avatar size={48} {...props} className='cu-mr-2' />;
  };

  private getRightsConfigurations = () => {
    const {
      requestRights: {
        configurations: { byId },
      },
    } = this.props;

    return Object.entries(byId).map(([configurationId, configuration]) => ({
      label: configuration.name,
      value: `${configurationId}`,
    }));
  };
}

const RotatingRightsRequests = styled.div`
  background-color: ${({ theme }) => theme.colors.gray00};
  margin-top: ${({ theme }) => theme.spacings[2]};
  position: relative;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.gray3};
  padding: ${({ theme }) =>
    `${theme.spacings[3]} ${theme.spacings[6]} ${theme.spacings[3]} ${theme.spacings[3]}`};
`;

const RefreshIcon = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;

  .cu-icon {
    width: 10px;
    height: 10px;
  }
`;

const RotatingRightsRequestsContent = styled.div`
  display: flex;
  align-items: start;
  font-size: 12px;
  height: ${({ theme }) => theme.spacings[7]};
  overflow-y: auto;
`;

const MessageLoadingFiller = styled.div`
  width: 100%;
  ${({ theme }) => `
    height: ${theme.spacings[7]};
    margin-top: ${theme.spacings[2]};
  `}
`;

const SelectLoadingFiller = styled.div`
  width: 100%;
  height: ${({ theme }) => theme.spacings[5]};
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RotatingRightsMessages);
